/* eslint-disable max-lines-per-function */
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import Big from 'big.js';
import Tooltip from 'components/Tooltip/Tooltip';
import { FormikProps } from 'formik';
import t from 'utils/translationHelper';

import { CurrencyAccountDto } from '@alpha/currency-accounts-dtos';
import {
  DrawdownFundingMethod,
  DrawdownSubmissionRequest,
  MassDrawdownValidationDto,
  TradeDto,
} from '@alpha/fx-dtos';
import { ActionButton } from '@alpha/ui-lib/ui/button';
import { Checkbox } from '@alpha/ui-lib/ui/Checkbox/Checkbox';
import { Box, Typography } from '@alpha/ui-lib/ui/external';
import { StyledKeyValueTable, TKeyValue } from '@alpha/ui-lib/ui/table';
import { StyledTabsWrapper } from '@alpha/ui-lib/ui/Tabs';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faLock } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AlphaLogo from '../../../../../assets/alphaGreenLogo.svg';
import useAuthorization from '../../../../../hooks/useAuthorization';
import { UserRole } from '../../../../../models/user';
import { formatNumber } from '../../../../../utils/currency.helpers';
import formatIsoDate from '../../../../../utils/formatIsoDate';
import mapDrawdownFundingMethodToFriendlyName from '../../../../../utils/fxTrades/mapDrawdownFundingMethodToFriendlyName';
import { FormikType, MassDrawdownRequestForm } from '../formData';
import { Section } from '../NewMassDrawdownDrawer';
import Summary from '../Summary';

import useStyles from './index.styles';

type Props = {
  drawdownRequestForm: MassDrawdownRequestForm;
  validatedDrawdownsResponse?: MassDrawdownValidationDto;
  submissionForm: FormikProps<DrawdownSubmissionRequest> | undefined;
  handleFormSubmit: (e: any) => void;
  handleSetSection: (_section: Section) => void;
  paymentValues: FormikType;
  sellCurrencyAccount: CurrencyAccountDto;
  buyCurrencyAccount: CurrencyAccountDto;
  sendEmailNotification: boolean;
  setSendEmailNotification: Dispatch<SetStateAction<boolean>>;
  selectedTrades: TradeDto[];
};

const DrawdownConfirmation: React.FC<Props> = ({
  handleFormSubmit,
  handleSetSection,
  paymentValues,
  sellCurrencyAccount,
  buyCurrencyAccount,
  drawdownRequestForm,
  validatedDrawdownsResponse,
  submissionForm,
  sendEmailNotification,
  setSendEmailNotification,
  selectedTrades,
}) => {
  const [tabIndex, setTabIndex] = useState<number>(0);

  const classes = useStyles();

  const [drawdownData, setDrawdownData] = useState<TKeyValue[]>([]);
  const [fundingData, setFundingData] = useState<TKeyValue[]>([]);
  const [accountDetailData, setAccountDetailData] = useState<TKeyValue[]>([]);
  const { authorized: authorizedPaymentApproverOwn } = useAuthorization([
    [UserRole.PAYMENTS_APPROVER_OWN],
  ]);

  const tabTitles = (): string[] => {
    const tabs = [t('drawdown_details')];

    if (paymentValues && paymentValues?.payments?.length! > 0) {
      tabs.push(`${t('payments')} (${paymentValues?.payments?.length || 0})`);
    }
    return tabs;
  };

  const getTotalSellAmount = () => {
    const totalSell = validatedDrawdownsResponse?.validatedDrawdowns?.reduce(
      (total, drawdown) => total.plus(drawdown.sellAmount || 0), new Big(0),
    ).toNumber();
    return totalSell;
  };

  const getTotalBuyAmount = () => {
    const totalBuy = validatedDrawdownsResponse?.validatedDrawdowns?.reduce(
      (total, drawdown) => total.plus(drawdown.buyAmount || 0), new Big(0),
    ).toNumber();
    return totalBuy;
  };

  const getTotalMarginReleased = () => {
    const totalMargin = validatedDrawdownsResponse?.validatedDrawdowns?.reduce(
      (total, drawdown) => total.plus(drawdown.marginReleased || 0), new Big(0),
    ).toNumber();
    return totalMargin;
  };

  const getTotalFinalRemittedAmount = () => {
    const totalFinalRemittedAmount = validatedDrawdownsResponse?.validatedDrawdowns?.reduce(
      (total, drawdown) => total.plus(drawdown.finalRemittedAmount || 0), new Big(0),
    ).toNumber();
    return totalFinalRemittedAmount;
  };

  const getTotalSoldAmountDue = () => {
    const totalSoldAmount = validatedDrawdownsResponse?.validatedDrawdowns?.reduce(
      (total, drawdown) => total.plus(drawdown.finalSoldAmountDue || 0), new Big(0),
    ).toNumber();
    return totalSoldAmount;
  };

  const getContractNumber = (id: string): string => {
    const trade = selectedTrades?.find((tr) => tr.id === id);
    return trade?.contractNumber || '';
  };

  useEffect(() => {
    setDrawdownData([
      {
        key: t('number_of_forward_contracts'),
        value: (
          <Tooltip
            title={validatedDrawdownsResponse?.validatedDrawdowns?.map(
              (drawdown) => (
                <div>
                  <span
                    style={{
                      fontWeight: '400',
                      fontSize: '12px',
                      color: '#1E8777',
                    }}
                  >
                    {' '}
                    {getContractNumber(drawdown.tradeId || '-')}
                  </span>
                  -
                  <span style={{ marginLeft: '4px' }}>
                    {`${formatNumber(+drawdown.sellAmount, 2)} ${
                      drawdownRequestForm?.sellCurrencyCode
                    }  |  ${formatNumber(+drawdown.buyAmount, 2)} ${
                      drawdownRequestForm?.buyCurrencyCode
                    }`}
                  </span>
                </div>
              ),
            )}
            placement="left-start"
          >
            <div>
              {selectedTrades?.length ?? '0'}
              <span className={classes.infoIcon}>
                <FontAwesomeIcon size="xs" icon={faInfoCircle as IconProp} />
              </span>
            </div>
          </Tooltip>
        ),
      },
      {
        key: t('sell_currency'),
        value: sellCurrencyAccount.currency
          ? `${sellCurrencyAccount?.currency}`
          : selectedTrades[0]?.soldCurrencyCode || '-',
        boldValue: true,
      },
      {
        key: t('sell_amount'),
        value: getTotalSellAmount() || '-',
        currency: selectedTrades[0]?.soldCurrencyCode,
        boldValue: true,
      },
      {
        key: t('weighted_rate'),
        value: formatNumber(drawdownRequestForm.weightedRate, 4) || '-',
        currency: '',
        boldValue: true,
      },
      {
        key: t('buy_currency'),
        value: buyCurrencyAccount?.currency
          ? `${buyCurrencyAccount?.currency}`
          : selectedTrades[0]?.buyCurrencyCode || '-',
        boldValue: true,
      },
      {
        key: t('buy_amount'),
        value: getTotalBuyAmount() || '-',
        currency: selectedTrades[0]?.buyCurrencyCode,
        boldValue: true,
      },
      {
        key: t('value_date'),
        value: formatIsoDate(drawdownRequestForm?.valueDate) || '-',
      },
    ]);

    const currentFundingData: TKeyValue[] = [
      {
        key: t('funding_method'),
        value: t(
          mapDrawdownFundingMethodToFriendlyName(
            drawdownRequestForm.fundingMethod,
          ),
        ),
      },
      {
        key: t('margin_released'),
        value: getTotalMarginReleased() || 0,
        currency:
          validatedDrawdownsResponse?.validatedDrawdowns?.[0]
            .initialMarginCurrencyCode,
      },
    ];

    if (
      validatedDrawdownsResponse?.validatedDrawdowns?.[0]
        .initialMarginCurrencyCode === drawdownRequestForm?.buyCurrencyCode
    ) {
      currentFundingData.push({
        key: t('final_remitted_amount'),
        value: getTotalFinalRemittedAmount() || 0,
        currency:
          validatedDrawdownsResponse?.validatedDrawdowns?.[0]
            .initialMarginCurrencyCode,
      });
    } else {
      currentFundingData.push({
        key: t('final_sold_amount_due'),
        value: getTotalSoldAmountDue() || 0,
        currency: drawdownRequestForm.sellCurrencyCode,
      });
    }

    setFundingData(currentFundingData);
  }, [drawdownRequestForm, validatedDrawdownsResponse]);

  const updateAccountDetail = (currencyAccount: CurrencyAccountDto) => {
    const currentAccountDetailData = [];
    currentAccountDetailData.push({
      key: t('account_name'),
      value: currencyAccount.accountName || '-',
      boldValue: true,
    });
    if (currencyAccount.accountNumber) {
      currentAccountDetailData.push({
        key: t('account_number'),
        value: currencyAccount.accountNumber || '-',
        boldValue: true,
      });
    }
    if (currencyAccount.sortCode) {
      currentAccountDetailData.push({
        key: t('national_bank_code'),
        value: currencyAccount.sortCode || '-',
        boldValue: true,
      });
    }
    if (currencyAccount.iban) {
      currentAccountDetailData.push({
        key: t('iban'),
        value: currencyAccount.iban || '-',
        boldValue: true,
      });
    }
    if (currencyAccount.swift) {
      currentAccountDetailData.push({
        key: 'SWIFT',
        value: currencyAccount.swift || '-',
        boldValue: true,
      });
    }
    if (currencyAccount.reference) {
      currentAccountDetailData.push({
        key: t('reference'),
        value: currencyAccount.reference || '-',
        boldValue: true,
      });
    }

    setAccountDetailData(currentAccountDetailData);
  };

  useEffect(() => {
    updateAccountDetail(sellCurrencyAccount);
  }, [sellCurrencyAccount]);

  return (
    <>
      <div
        className={
          drawdownRequestForm.fundingMethod
          === DrawdownFundingMethod.WIRE_TRANSFER && tabIndex === 0
            ? classes.wireTransferContainer
            : classes.container
        }
      >
        <StyledTabsWrapper
          testId="drawdown-summary-tabs-wrapper"
          tabTitles={tabTitles()}
          tabIndex={tabIndex}
          handleChange={(selectedTabIndex: number) => setTabIndex(selectedTabIndex)}
        />

        {tabIndex === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div className={classes.drawdownDetailTable}>
              <StyledKeyValueTable
                testId="drawdown-data-table"
                data={drawdownData}
              />
            </div>

            <div className={classes.drawdownFundingTable}>
              <StyledKeyValueTable
                testId="drawdown-funding-table"
                data={fundingData}
              />
            </div>

            {drawdownRequestForm.fundingMethod
              === DrawdownFundingMethod.WIRE_TRANSFER && (
              <div className={classes.wireTransferGuide}>
                <Typography className={classes.subHeading2}>
                  {t('wire_transfer_details')}
                </Typography>
                <span>
                  {t('please_ensure_funds_are_remitted_to_alpha_bank')}
                </span>

                <Typography className={classes.subHeading2} component="div">
                  <img
                    alt="Alpha Account"
                    src={AlphaLogo}
                    style={{ verticalAlign: 'top', marginRight: '8px' }}
                  />
                  {' '}
                  {`${sellCurrencyAccount.currencyCode} - ${
                    sellCurrencyAccount.friendlyName
                    || sellCurrencyAccount.accountName
                    || ''
                  }`}
                  <div className={classes.accountDetailTable}>
                    <StyledKeyValueTable
                      testId="account-detail-table"
                      data={accountDetailData}
                    />
                  </div>
                </Typography>
              </div>
            )}
          </div>
        )}

        {tabIndex === 1 && <Summary values={paymentValues} />}
        {tabIndex === 1 && !authorizedPaymentApproverOwn && (
          <div
            style={{
              position: 'relative',
              marginTop: 'auto',
            }}
          >
            <Checkbox
              testId="send-email-notifcation"
              label={t('send_an_email_notification_to_all_payment_approvers')}
              checked={sendEmailNotification}
              onChange={(event) => {
                setSendEmailNotification(event.target.checked);
              }}
            />
          </div>
        )}
        <Box className={classes.buttonsContainer}>
          <ActionButton
            style={{
              background: '#F7F7F7',
              width: '134px',
              color: '#212529',
              whiteSpace: 'nowrap',
            }}
            onClick={() => {
              handleSetSection('Settlement');
            }}
            testId="mass-drawdown-back-to-settlement-btn"
          >
            {t('back_to_settlement')}
          </ActionButton>
          <ActionButton
            fullWidth
            disabled={!submissionForm?.isValid || submissionForm?.isSubmitting}
            onClick={handleFormSubmit}
            size="medium"
            testId="submit-mass-drawdown-btn"
            className="submit-mass-drawdown"
          >
            {t('submit_mass_drawdown')}
            {'  '}
            <FontAwesomeIcon
              icon={faLock as IconProp}
              style={{ paddingLeft: '4px' }}
            />
          </ActionButton>
        </Box>
      </div>
    </>
  );
};

export default DrawdownConfirmation;
